import { useState, useEffect } from 'react';
import { D, st, apiGet, apiPost } from '../constants';

const PER_SEAT = 13;
const BASE_SEATS = 15;
const DEFAULT_NOTE = 'Your custom plan includes full access to all AmpBooks features — AI bidding, invoicing, scheduling, payroll, permits, and more — for your entire team. Priority support is included. Your subscription renews automatically and seats can be adjusted anytime as your team grows.';

export default function CustomBilling() {
  const [companies, setCompanies]         = useState([]);
  const [companyId, setCompanyId]         = useState('');
  const [basePrice, setBasePrice]         = useState(249);
  const [seats, setSeats]                 = useState(15);
  const [notes, setNotes]                 = useState(DEFAULT_NOTE);
  const [billingInterval, setBillingInterval] = useState('month');
  const [sending, setSending]             = useState(false);
  const [result, setResult]               = useState(null);

  useEffect(() => {
    apiGet('/admin/companies').then(d => setCompanies(d.companies || []));
  }, []);

  const extraSeats = Math.max(0, Number(seats) - BASE_SEATS);
  const monthlyTotal = Number(basePrice) + extraSeats * PER_SEAT;
  const yearlyTotal  = monthlyTotal * 10;
  const displayTotal = billingInterval === 'year' ? yearlyTotal : monthlyTotal;
  const displayLabel = billingInterval === 'year' ? '/yr' : '/mo';

  async function send() {
    if (!companyId) return alert('Select a company');
    setSending(true);
    setResult(null);
    try {
      const res = await apiPost('/admin/custom-billing', {
        companyId, basePrice: Number(basePrice), seats: Number(seats), notes, interval: billingInterval,
      });
      setResult({ ok: true, total: res.totalCharged, interval: res.billingInterval });
    } catch (e) {
      setResult({ ok: false, error: e.message });
    } finally {
      setSending(false);
    }
  }

  const inp = { background: D.bg, color: D.text, border: '1px solid ' + D.border, borderRadius: 6, padding: '8px 12px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const lbl = { display: 'block', color: D.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 };
  const tabBtn = (active) => ({ padding: '6px 18px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer', background: active ? D.accent : D.bg, color: active ? '#fff' : D.muted });

  return (
    <div style={{ maxWidth: 520 }}>
      <h2 style={{ color: D.accent, marginBottom: 20 }}>Custom Billing</h2>
      <div style={{ background: D.card, border: '1px solid ' + D.border, borderRadius: 12, padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

        <div>
          <label style={lbl}>Company</label>
          <select value={companyId} onChange={e => setCompanyId(e.target.value)} style={inp}>
            <option value="">— Select company —</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
          </select>
        </div>

        <div>
          <label style={lbl}>Billing Interval</label>
          <div style={{ display: 'flex', gap: 8, background: D.bg, borderRadius: 8, padding: 4, border: '1px solid ' + D.border }}>
            <button style={tabBtn(billingInterval === 'month')} onClick={() => setBillingInterval('month')}>Monthly</button>
            <button style={tabBtn(billingInterval === 'year')} onClick={() => setBillingInterval('year')}>
              Yearly <span style={{ fontSize: 11, color: billingInterval === 'year' ? '#22c55e' : D.muted, marginLeft: 4 }}>2 months free</span>
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={lbl}>Base Price ($/mo)</label>
            <input type="number" min="1" value={basePrice} onChange={e => setBasePrice(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>Total Seats</label>
            <input type="number" min="1" value={seats} onChange={e => setSeats(e.target.value)} style={inp} />
          </div>
        </div>

        {extraSeats > 0 && (
          <div style={{ fontSize: 12, color: D.muted, background: D.bg, borderRadius: 6, padding: '8px 12px', lineHeight: 1.6 }}>
            $249 base (15 seats) + {extraSeats} extra seat{extraSeats !== 1 ? 's' : ''} × $13 = <strong style={{ color: D.text }}>${extraSeats * PER_SEAT}/mo extra</strong>
          </div>
        )}

        <div style={{ borderTop: '1px solid ' + D.border, paddingTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ color: D.muted, fontSize: 13 }}>Total Charged</span>
            <span style={{ color: D.accent, fontSize: 24, fontWeight: 700 }}>${displayTotal}{displayLabel}</span>
          </div>
          {billingInterval === 'year' && (
            <div style={{ fontSize: 12, color: '#22c55e', textAlign: 'right', marginTop: 4 }}>
              Saves ${monthlyTotal * 2}/yr vs monthly
            </div>
          )}
        </div>

        <div>
          <label style={lbl}>Notes (shown in email)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
            style={{ ...inp, resize: 'vertical' }} />
        </div>

        <button onClick={send} disabled={sending || !companyId}
          style={{ ...st.btn(), padding: '12px 24px', fontSize: 14, fontWeight: 700, background: D.accent, color: '#fff', opacity: (!companyId || sending) ? 0.5 : 1 }}>
          {sending ? 'Sending…' : '✉️ Send Payment Link'}
        </button>

        {result?.ok && (
          <div style={{ background: '#22c55e22', color: '#22c55e', borderRadius: 8, padding: '12px 16px', fontSize: 13 }}>
            ✓ Payment link sent — ${result.total}/{result.interval === 'year' ? 'yr' : 'mo'}. Plan activates automatically on payment.
          </div>
        )}
        {result?.ok === false && (
          <div style={{ background: D.danger + '22', color: D.danger, borderRadius: 8, padding: '12px 16px', fontSize: 13 }}>
            Error: {result.error}
          </div>
        )}
      </div>
    </div>
  );
}
